// ========================
// Prime Exoneree (Tax-exempt bonus)
// ========================

export interface PrimeExoneree {
  id: number;
  nom: string;
  condition?: string;
  is_active: boolean;
  // When false, the prime is paid at full amount regardless of monthly proration.
  prorate: boolean;
  // One-shot primes (e.g. Adha) apply only to the (mois, annee) below.
  one_shot: boolean;
  mois?: number | null;
  annee?: number | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
}

export interface PrimeExonereeCreate {
  nom: string;
  condition?: string;
  is_active?: boolean;
  prorate?: boolean;
  one_shot?: boolean;
  mois?: number | null;
  annee?: number | null;
}

export interface PrimeExonereeUpdate {
  nom?: string;
  condition?: string;
  is_active?: boolean;
  prorate?: boolean;
  one_shot?: boolean;
  mois?: number | null;
  annee?: number | null;
}

export interface PrimeExonereeListResponse {
  items: PrimeExoneree[];
  total: number;
}

// ========================
// Prime Non Exoneree (Taxable bonus)
// ========================

export interface PrimeNonExoneree {
  id: number;
  nom: string;
  condition?: string;
  is_active: boolean;
  prorate: boolean;
  one_shot: boolean;
  mois?: number | null;
  annee?: number | null;
  // Agreed in days (indemnité compensatrice de congé…): the PAIE tab asks for a
  // day count and derives the montant instead of taking a typed figure.
  base_jours?: boolean;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
}

export interface PrimeNonExonereeCreate {
  nom: string;
  condition?: string;
  is_active?: boolean;
  prorate?: boolean;
  one_shot?: boolean;
  mois?: number | null;
  annee?: number | null;  base_jours?: boolean;
}

export interface PrimeNonExonereeUpdate {
  nom?: string;
  condition?: string;
  is_active?: boolean;
  prorate?: boolean;
  one_shot?: boolean;
  mois?: number | null;
  annee?: number | null;  base_jours?: boolean;
}

export interface PrimeNonExonereeListResponse {
  items: PrimeNonExoneree[];
  total: number;
}

// ========================
// Cotisation Sociale (Social contribution)
// ========================

export interface CotisationSociale {
  id: number;
  nom: string;
  taux: number; // Rate in percentage (e.g., 4.48 for 4.48%)
  condition?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
}

export interface CotisationSocialeCreate {
  nom: string;
  taux: number;
  condition?: string;
  is_active?: boolean;
}

export interface CotisationSocialeUpdate {
  nom?: string;
  taux?: number;
  condition?: string;
  is_active?: boolean;
}

export interface CotisationSocialeListResponse {
  items: CotisationSociale[];
  total: number;
}

// ========================
// Employee Primes (Link tables)
// ========================

export interface EmployeePrimeExoneree {
  id: number;
  employee_id: number;
  prime_id: number;
  prime_nom?: string;
  // When false the prime is paid in full regardless of the month's proration factor.
  prorate?: boolean;
  // One-shot primes only apply to (prime_mois, prime_annee).
  one_shot?: boolean;
  prime_mois?: number | null;   // from the prime definition (legacy fallback)
  prime_annee?: number | null;
  // The period THIS assignment runs on — one-shot primes only. Wins over the
  // definition's, which is only a fallback for rows created before the move.
  mois?: number | null;
  annee?: number | null;
  // Fenêtre de validité de CETTE attribution. Une ligne = une période : une
  // prime arrêtée puis reprise est deux lignes, si bien que la réactivation ne
  // peut pas remplir rétroactivement les mois d'interruption.
  date_debut?: string | null;
  date_fin?: string | null;
  montant: number;
  created_at?: string;
  updated_at?: string;
}

export interface EmployeePrimeNonExoneree {
  id: number;
  employee_id: number;
  prime_id: number;
  prime_nom?: string;
  prorate?: boolean;
  one_shot?: boolean;
  prime_mois?: number | null;
  prime_annee?: number | null;
  base_jours?: boolean;
  // The period THIS assignment runs on — one-shot primes only.
  mois?: number | null;
  annee?: number | null;
  // Fenêtre de validité de CETTE attribution. Une ligne = une période : une
  // prime arrêtée puis reprise est deux lignes, si bien que la réactivation ne
  // peut pas remplir rétroactivement les mois d'interruption.
  date_debut?: string | null;
  date_fin?: string | null;
  montant: number;
  // Days the montant was derived from — kept so the payslip can justify it.
  nombre_jours?: number | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Whether an employee's prime assignment counts for a given payroll month.
 *
 * Mirror of `prime_applies()` in backend/app/services/prime_period.py — change
 * both together. Used by Base de Calcul Paie (the real month) and by the fiche's
 * PAIE recap (the current month), so the two agree on which primes are live.
 *
 * A recurring prime applies while THIS assignment's window covers the month,
 * compared at month granularity; NULL on either side means unbounded. A
 * one-shot prime applies only on its own month — the assignment's period first,
 * the definition's only as the legacy fallback — and one with no period at all
 * applies nowhere, deliberately.
 */
export function primeAppliesToMonth(
  p: {
    one_shot?: boolean;
    mois?: number | null;
    annee?: number | null;
    prime_mois?: number | null;
    prime_annee?: number | null;
    date_debut?: string | null;
    date_fin?: string | null;
  },
  mois: number,
  annee: number
): boolean {
  if (!p?.one_shot) {
    const inMonths = (iso: string) => {
      const d = new Date(iso);
      return d.getFullYear() * 12 + d.getMonth();
    };
    const target = annee * 12 + (mois - 1);
    if (p.date_debut && target < inMonths(p.date_debut)) return false;
    if (p.date_fin && target > inMonths(p.date_fin)) return false;
    return true;
  }
  const pMois = p.mois ?? p.prime_mois;
  const pAnnee = p.annee ?? p.prime_annee;
  if (pMois == null || pAnnee == null) return false;
  return Number(pMois) === mois && Number(pAnnee) === annee;
}

export interface EmployeePrimeCreate {
  // Id of the row being kept. Sent back so the save updates it in place instead
  // of recreating it, which would discard its validity period.
  id?: number | null;
  prime_id: number;
  montant: number;
  nombre_jours?: number | null;
  mois?: number | null;
  annee?: number | null;
  date_debut?: string | null;
  date_fin?: string | null;
}

export interface EmployeeCotisationSociale {
  id: number;
  employee_id: number;
  cotisation_id: number;
  cotisation_nom?: string;
  cotisation_taux?: number;
  created_at?: string;
  updated_at?: string;
}

export interface EmployeeCotisationSocialeCreate {
  cotisation_id: number;
}

export interface EmployeePrimesUpdate {
  primes_exonerees: EmployeePrimeCreate[];
  primes_non_exonerees: EmployeePrimeCreate[];
  cotisations_sociales: EmployeeCotisationSocialeCreate[];
}

export interface EmployeePrimesResponse {
  primes_exonerees: EmployeePrimeExoneree[];
  primes_non_exonerees: EmployeePrimeNonExoneree[];
  cotisations_sociales: EmployeeCotisationSociale[];
  total_exonerees: number;
  total_non_exonerees: number;
}

// ========================
// Dropdown types
// ========================

export interface PrimeDropdownItem {
  id: number;
  nom: string;
}

export interface CotisationDropdownItem {
  id: number;
  nom: string;
  taux: number;
}

// ========================
// Moroccan IR (Income Tax) Brackets 2024
// ========================

export interface IRBracket {
  min: number;
  max: number;
  rate: number; // Rate in decimal (e.g., 0.10 for 10%)
  deduction: number;
}

// CNSS ceiling (plafond) - 6,000 DH/month
export const CNSS_CEILING = 6000;

// Moroccan IR tax brackets 2026 (monthly)
export const MOROCCAN_IR_BRACKETS: IRBracket[] = [
  { min: 0, max: 3333.33, rate: 0, deduction: 0 },
  { min: 3333.34, max: 5000, rate: 0.10, deduction: 333.33 },
  { min: 5001, max: 6666.67, rate: 0.20, deduction: 833.33 },
  { min: 6667, max: 8333.33, rate: 0.30, deduction: 1500 },
  { min: 8333.34, max: 15000, rate: 0.34, deduction: 1833.33 },
  { min: 15001, max: Infinity, rate: 0.37, deduction: 2283.33 },
];

// ========================
// Payroll Calculation Helper Types
// ========================

export interface PayrollCalculation {
  // Base salary (A)
  salaireBase: number;

  // Primes Non Exonerees (B)
  totalPrimesNonExonerees: number;
  primesNonExonereesDetails: { nom: string; montant: number; prorate?: boolean }[];

  // Primes Exonerees (C)
  totalPrimesExonerees: number;
  primesExonereesDetails: { nom: string; montant: number; prorate?: boolean }[];

  // Salaire Brut Global (D = A + B + C)
  salaireBrutGlobal: number;

  // Salaire Brut Imposable (E = D - C)
  salaireBrutImposable: number;

  // Total Cotisations Sociales (F)
  totalCotisations: number;
  cotisationsDetails: { nom: string; taux: number; montant: number; isCNSS: boolean; baseForCalculation: number }[];

  // Frais Professionnel (G = MIN(E * rate%, max) where rate/max depends on E)
  // E <= 6500: 35%, max 2500 | E > 6500: 25%, max 2916.67
  fraisProfessionnel: number;
  fraisProfessionnelRate: number; // The rate used (0.35 or 0.25)
  fraisProfessionnelMax: number; // The max used (2500 or 2916.67)

  // Salaire Net Imposable (H = E - F - G)
  salaireNetImposable: number;

  // Annual Salaire Net Imposable for IR calculation
  salaireNetImposableAnnuel: number;

  // IR Brut (I) - (H × Taux) - Somme à déduire
  irBrut: number;

  // Deduction for dependents (J) - nbrPersonnes × 30, max 180
  deductionChargeFamille: number;
  nbrPersonnesACharge: number;

  // IR Net (K) - IR Brut - Deduction charge famille
  ir: number;
  irBracket?: IRBracket;

  // Net A Payer = Salaire Brut Global - Cotisations - IR
  netAPayer: number;

  // Monthly dynamic fields (only present when calculated via calculateMonthlyPayroll)
  deductionAbsence?: number;
  gainJourFerie?: number;
  gainConge?: number;
  primeAnciennete?: number;
  tauxAnciennete?: number;
  heuresAbsence?: number;
  joursFerie?: number;
  joursConge?: number;
  heuresPresence?: number;
  totalPrimesExonereesProratees?: number;
  totalPrimesNonExonereesProratees?: number;
  prorationFactor?: number;
}

// ========================
// Payroll Calculation Functions
// ========================

/**
 * Seniority rate for a hire date, measured at `asOf` (Moroccan labor law scale).
 *
 * Mirror of `compute_anciennete_info()` in backend/app/api/routes/paie.py —
 * including the "+1 day" rule: a year of service counts as complete the day
 * BEFORE the anniversary, so someone hired 01/09/2021 is at five years on
 * 31/08/2026. Change both together.
 *
 * The paie rates a month with the seniority the employee had at the END of that
 * month; this copy serves the fiche, a "today" view, so the caller passes the
 * reference date it means.
 */
export function tauxAncienneteAt(
  dateEmbauche: string | Date | null | undefined,
  asOf: Date = new Date()
): number {
  if (!dateEmbauche) return 0;
  const hire = dateEmbauche instanceof Date ? dateEmbauche : new Date(dateEmbauche);
  if (isNaN(hire.getTime())) return 0;

  // Completed years between the hire date and the day AFTER `asOf`.
  const ref = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate() + 1);
  let annees = ref.getFullYear() - hire.getFullYear();
  const monthDiff = ref.getMonth() - hire.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < hire.getDate())) annees--;
  if (annees < 0) return 0;

  if (annees >= 25) return 0.25;
  if (annees >= 20) return 0.2;
  if (annees >= 12) return 0.15;
  if (annees >= 5) return 0.1;
  if (annees >= 2) return 0.05;
  return 0;
}

export function calculatePayroll(
  salaireBase: number,
  primesExonerees: { montant: number; nom?: string }[],
  primesNonExonerees: { montant: number; nom?: string }[],
  cotisations: { taux: number; nom: string }[],
  nbrPersonnesACharge: number = 0,
  situationFamiliale: string = 'CELIBATAIRE',
  tauxAnciennete: number = 0
): PayrollCalculation {
  // A - Base salary
  const A = salaireBase;

  // B - Sum of taxable bonuses
  const B = primesNonExonerees.reduce((sum, p) => sum + p.montant, 0);

  // C - Sum of tax-exempt bonuses
  const C = primesExonerees.reduce((sum, p) => sum + p.montant, 0);

  // Prime d'ancienneté — on the full-month base, since this is the theoretical
  // fiche view (no pointage). calculateMonthlyPayroll rates the worked base
  // (SB - absence + JF + JC) instead; both are `base x taux`.
  const primeAnciennete = A * tauxAnciennete;

  // D - Gross global salary
  const D = A + B + C + primeAnciennete;

  // E - Gross taxable salary (excludes tax-exempt bonuses)
  const E = D - C;

  // F - Social contributions (calculated on E, with CNSS ceiling)
  const cotisationsDetails = cotisations.map(c => {
    // CNSS has a ceiling of 6,000 DH - check if cotisation name contains "CNSS"
    const isCNSS = c.nom.toUpperCase().includes('CNSS');
    const baseForCalculation = isCNSS ? Math.min(E, CNSS_CEILING) : E;
    return {
      nom: c.nom,
      taux: c.taux,
      montant: Math.round((baseForCalculation * c.taux / 100) * 100) / 100,
      isCNSS,
      baseForCalculation
    };
  });
  const F = cotisationsDetails.reduce((sum, c) => sum + c.montant, 0);

  // G - Professional expenses
  // Case A: E <= 6500 -> Rate: 35%, Max: 2500 DH
  // Case B: E > 6500 -> Rate: 25%, Max: 2916.67 DH
  const fraisRate = E <= 6500 ? 0.35 : 0.25;
  const fraisMax = E <= 6500 ? 2500 : 2916.67;
  const G = Math.min(E * fraisRate, fraisMax);

  // H - Net taxable salary (monthly)
  const H = E - F - G;

  // Annual net taxable salary for IR calculation
  const annualH = H * 12;

  // I - IR Brut calculation (monthly)
  // IR Brut = (H × Taux) - Somme à déduire
  let irBrut = 0;
  let irBracket: IRBracket | undefined;

  for (const bracket of MOROCCAN_IR_BRACKETS) {
    if (H >= bracket.min && H <= bracket.max) {
      // IR Brut = (H × Taux) - Somme à déduire
      irBrut = H * bracket.rate - bracket.deduction;
      irBracket = bracket;
      break;
    }
  }

  // Ensure IR Brut is not negative
  irBrut = Math.max(0, Math.round(irBrut * 100) / 100);

  // J - Deduction for family dependents (41.67 DH per person, max 250 DH) - 2026
  // Only married employees add +1 for spouse; single/divorced/widowed add +0
  const conjointDeduction = situationFamiliale === 'MARIE' ? 1 : 0;
  const totalPersonnes = nbrPersonnesACharge + conjointDeduction;
  const deductionChargeFamille = Math.min(totalPersonnes * 41.67, 250);

  // K - IR Net = Max(0, IR Brut - Charges de Famille)
  let ir = Math.max(0, irBrut - deductionChargeFamille);
  ir = Math.round(ir * 100) / 100;

  // Net to pay = Gross - Contributions - IR
  const netAPayer = Math.round(D - F - ir);

  return {
    salaireBase: A,
    totalPrimesNonExonerees: B,
    primesNonExonereesDetails: primesNonExonerees.filter(p => p.montant > 0).map(p => ({ nom: p.nom || '', montant: p.montant })),
    totalPrimesExonerees: C,
    primesExonereesDetails: primesExonerees.filter(p => p.montant > 0).map(p => ({ nom: p.nom || '', montant: p.montant })),
    salaireBrutGlobal: D,
    salaireBrutImposable: E,
    totalCotisations: Math.round(F * 100) / 100,
    cotisationsDetails,
    fraisProfessionnel: Math.round(G * 100) / 100,
    fraisProfessionnelRate: fraisRate,
    fraisProfessionnelMax: fraisMax,
    salaireNetImposable: Math.round(H * 100) / 100,
    salaireNetImposableAnnuel: Math.round(annualH * 100) / 100,
    irBrut,
    deductionChargeFamille,
    nbrPersonnesACharge: totalPersonnes,
    ir,
    irBracket,
    netAPayer,
    primeAnciennete: Math.round(primeAnciennete * 100) / 100,
    tauxAnciennete
  };
}

// ========================
// Monthly Payroll Calculation (with pointage data)
// ========================

const MONTHLY_HOURS = 191;
const HOURS_PER_DAY_CALC = 191 / 26; // ~7.3461

export interface PointageSummaryInput {
  heures_absence: number;
  jours_ferie: number;
  jours_conge_paye: number;
  jours_conge_maladie: number;
  jours_conge_mariage: number;
  jours_conge_deces: number;
  jours_conge_naissance: number;
  heures_presence: number;
  jours_present: number;
  jours_absent: number;
  heures_supplementaires: number;
}

export function calculateMonthlyPayroll(
  salaireBase: number,
  declarationJournaliere: number,
  primesExonerees: { montant: number; nom?: string; prorate?: boolean }[],
  primesNonExonerees: { montant: number; nom?: string; prorate?: boolean }[],
  cotisations: { taux: number; nom: string }[],
  nbrPersonnesACharge: number,
  situationFamiliale: string,
  pointage: PointageSummaryInput,
  tauxAnciennete: number,
): PayrollCalculation {
  const SB = salaireBase;

  // 1. Absence deduction: (191 - HP) × (SB/191)
  // Deducts ALL non-worked hours (absence + JF + congé) so JF is paid only once when added back
  const deductionAbsence = (MONTHLY_HOURS - pointage.heures_presence) * (SB / MONTHLY_HOURS);

  // 2. Jour Férié gain (guard div/0)
  const gainJF = declarationJournaliere > 0
    ? pointage.jours_ferie * (SB / declarationJournaliere)
    : 0;

  // 3. Congé gain - Only paid leave types (not CONGE_MALADIE which is covered by CNSS)
  const joursConge = pointage.jours_conge_paye
    + pointage.jours_conge_mariage
    + pointage.jours_conge_deces
    + pointage.jours_conge_naissance;
  const gainConge = declarationJournaliere > 0
    ? joursConge * (SB / declarationJournaliere)
    : 0;

  // 4. Proration factor based on actual hours
  const actualHours = pointage.heures_presence
    + (joursConge * HOURS_PER_DAY_CALC)
    + (pointage.jours_ferie * HOURS_PER_DAY_CALC);
  const prorationFactor = Math.min(actualHours / MONTHLY_HOURS, 1.0);

  // 5. Prorate primes — per-prime factor: primes with prorate=false take full amount.
  const primeFactor = (p: { prorate?: boolean }) =>
    p.prorate === false ? 1 : prorationFactor;
  const B_prorated = primesNonExonerees.reduce((sum, p) => sum + p.montant * primeFactor(p), 0);
  const C_prorated = primesExonerees.reduce((sum, p) => sum + p.montant * primeFactor(p), 0);

  // 6. Prime d'ancienneté
  const primeAnciennete = (SB - deductionAbsence + gainJF + gainConge) * tauxAnciennete;

  // 7. D - Salaire Brut Global
  const D = SB + gainJF + gainConge + B_prorated + C_prorated + primeAnciennete - deductionAbsence;

  // 8. E - Salaire Brut Imposable (remove tax-exempt primes)
  const E = D - C_prorated;

  // F - Social contributions (calculated on E, with CNSS ceiling)
  const cotisationsDetails = cotisations.map(c => {
    const isCNSS = c.nom.toUpperCase().includes('CNSS');
    const baseForCalculation = isCNSS ? Math.min(E, CNSS_CEILING) : E;
    return {
      nom: c.nom,
      taux: c.taux,
      montant: Math.round((baseForCalculation * c.taux / 100) * 100) / 100,
      isCNSS,
      baseForCalculation
    };
  });
  const F = cotisationsDetails.reduce((sum, c) => sum + c.montant, 0);

  // G - Professional expenses
  const fraisRate = E <= 6500 ? 0.35 : 0.25;
  const fraisMax = E <= 6500 ? 2500 : 2916.67;
  const G = Math.min(E * fraisRate, fraisMax);

  // H - Net taxable salary
  const H = E - F - G;

  // Annual net taxable salary for IR
  const annualH = H * 12;

  // I - IR Brut
  let irBrut = 0;
  let irBracket: IRBracket | undefined;
  for (const bracket of MOROCCAN_IR_BRACKETS) {
    if (H >= bracket.min && H <= bracket.max) {
      irBrut = H * bracket.rate - bracket.deduction;
      irBracket = bracket;
      break;
    }
  }
  irBrut = Math.max(0, Math.round(irBrut * 100) / 100);

  // J - Deduction for family dependents
  const conjointDeduction = situationFamiliale === 'MARIE' ? 1 : 0;
  const totalPersonnes = nbrPersonnesACharge + conjointDeduction;
  const deductionChargeFamille = Math.min(totalPersonnes * 41.67, 250);

  // K - IR Net = Max(0, IR Brut - Charges de Famille)
  let ir = Math.max(0, irBrut - deductionChargeFamille);
  ir = Math.round(ir * 100) / 100;

  // Net to pay = D - F - K
  const netAPayer = Math.round(D - F - ir);

  return {
    salaireBase: SB,
    totalPrimesNonExonerees: Math.round(B_prorated * 100) / 100,
    primesNonExonereesDetails: primesNonExonerees.filter(p => p.montant > 0).map(p => ({
      nom: p.nom || '',
      montant: Math.round(p.montant * primeFactor(p) * 100) / 100,
      prorate: p.prorate !== false,
    })),
    totalPrimesExonerees: Math.round(C_prorated * 100) / 100,
    primesExonereesDetails: primesExonerees.filter(p => p.montant > 0).map(p => ({
      nom: p.nom || '',
      montant: Math.round(p.montant * primeFactor(p) * 100) / 100,
      prorate: p.prorate !== false,
    })),
    salaireBrutGlobal: Math.round(D * 100) / 100,
    salaireBrutImposable: Math.round(E * 100) / 100,
    totalCotisations: Math.round(F * 100) / 100,
    cotisationsDetails,
    fraisProfessionnel: Math.round(G * 100) / 100,
    fraisProfessionnelRate: fraisRate,
    fraisProfessionnelMax: fraisMax,
    salaireNetImposable: Math.round(H * 100) / 100,
    salaireNetImposableAnnuel: Math.round(annualH * 100) / 100,
    irBrut,
    deductionChargeFamille,
    nbrPersonnesACharge: totalPersonnes,
    ir,
    irBracket,
    netAPayer,
    // Monthly-specific fields
    deductionAbsence: Math.round(deductionAbsence * 100) / 100,
    gainJourFerie: Math.round(gainJF * 100) / 100,
    gainConge: Math.round(gainConge * 100) / 100,
    primeAnciennete: Math.round(primeAnciennete * 100) / 100,
    tauxAnciennete,
    heuresAbsence: MONTHLY_HOURS - pointage.heures_presence,
    joursFerie: pointage.jours_ferie,
    joursConge,
    heuresPresence: pointage.heures_presence,
    totalPrimesExonereesProratees: Math.round(C_prorated * 100) / 100,
    totalPrimesNonExonereesProratees: Math.round(B_prorated * 100) / 100,
    prorationFactor: Math.round(prorationFactor * 10000) / 10000,
  };
}
